import type { Entity, PerformancePart, Persisted } from '@tunasong/schemas'
import { isSong, normalizeParts } from '@tunasong/schemas'
import { useState } from 'react'
import { usePerformanceConfig } from './performance-config.hook.js'
import type { PartResolution } from './resolve-parts.js'
import { resolveParts } from './resolve-parts.js'
import { useSongBand } from './song-band.hook.js'
import { useSongPerformanceSettings } from './song-performance-settings.hook.js'

const fullChart: PerformancePart[] = ['full']

/** Where a part change applies: the band set (or the default, outside a band), or this song only. */
export type PartsScope = 'band' | 'song'

export interface ChoosePartsOptions {
  /** Also save the choice as the profile default. Ignored outside a band, where the choice IS the default. */
  alsoDefault?: boolean
}

/**
 * While the song's settings row loads, an override may still arrive: hold back the picker and the default
 * notice so neither flashes up for a song that has one. The parts themselves keep rendering.
 */
const holdPrompts = (resolution: PartResolution): PartResolution => {
  if (resolution.status === 'ask') {
    return { status: 'resolving' }
  }
  if (resolution.status === 'resolved' && resolution.showDefaultNotice) {
    return { ...resolution, showDefaultNotice: false }
  }
  return resolution
}

/**
 * The parts a performer plays for the song in view, and the explicit choices that change them.
 *
 * Resolution: the person's override for this song, else their set for the song's band, else their profile
 * default, else ask (the picker). Only an explicit choice writes a level; no level is ever copied into
 * another implicitly. "This song only" is such a choice: it saves the current set as the override (D13).
 */
export const usePerformanceParts = (entity?: Persisted<Entity>) => {
  const songBand = useSongBand(entity)
  const { config, updatePerformanceConfig, hasLoaded: configLoaded } = usePerformanceConfig()
  /** Only a song carries an override; a page or folder in view reads and writes none. */
  const song = isSong(entity) ? entity : undefined
  const { songParts, setSongParts, frame, setFrame, transpose, isLoaded } = useSongPerformanceSettings(song)
  const songSettingsLoaded = !song || isLoaded
  /** A dismissed picker, remembered for this visit and for that band only. `bandId` null = personal songs. */
  const [dismissed, setDismissed] = useState<{ bandId: string | null } | null>(null)

  const band = songBand.status === 'band' ? songBand.band : null
  const sessionParts =
    songBand.status !== 'resolving' && dismissed && dismissed.bandId === (band?.id ?? null) ? fullChart : undefined
  const resolved = resolveParts({ songBand, config, songParts, sessionParts })
  /**
   * Until the user config loads it reads as empty: no default and no band sets. Asking then flashed the
   * picker up for a person who has a default, and it vanished when the profile arrived (#2247).
   */
  const resolution = songSettingsLoaded && configLoaded ? resolved : holdPrompts(resolved)
  /** Derived, so an override present on load opens "Apply to" on this song */
  const applyTo: PartsScope = songParts ? 'song' : 'band'

  /** Never blank the song: while the band resolves, or while asking, render the default (or Full chart). */
  const parts = resolution.status === 'resolved' ? resolution.parts : (normalizeParts(config.defaultParts) ?? fullChart)

  const chooseParts = (chosen: readonly PerformancePart[], options: ChoosePartsOptions = {}) => {
    const next = normalizeParts(chosen) ?? fullChart
    if (applyTo === 'song') {
      setSongParts(next)
      return
    }
    if (songBand.status === 'resolving') {
      return
    }
    if (!band) {
      updatePerformanceConfig({ defaultParts: next })
      return
    }
    updatePerformanceConfig({
      partsByBand: { ...config.partsByBand, [band.id]: next },
      ...(options.alsoDefault ? { defaultParts: next } : {}),
    })
  }

  /** "This song only" saves a copy of the set in view as the override at once (D13); the band deletes it. */
  const setApplyTo = (scope: PartsScope) => {
    if (scope === applyTo) {
      return
    }
    setSongParts(scope === 'song' ? [...parts] : undefined)
  }

  /** "Use band parts again": delete the override, so "Apply to" returns to the band. */
  const restoreBandParts = () => setSongParts(undefined)

  const dismissPicker = () => setDismissed({ bandId: band?.id ?? null })

  const acknowledgeNotice = () => {
    const acknowledged = config.acknowledgedDefaultBands ?? []
    if (!band || acknowledged.includes(band.id)) {
      return
    }
    updatePerformanceConfig({ acknowledgedDefaultBands: [...acknowledged, band.id] })
  }

  return {
    resolution,
    parts,
    applyTo,
    chooseParts,
    setApplyTo,
    restoreBandParts,
    dismissPicker,
    acknowledgeNotice,
    /** This song's Shapes | Concert frame (unset reads as Shapes), remembered per person per song. */
    frame,
    setFrame,
    /** The person's transpose for this song, for the header line. */
    transpose,
  }
}

export type PerformanceParts = ReturnType<typeof usePerformanceParts>
